import { createTheme } from '@mui/material/styles';

export const getAppTheme = (customSettings = {}) => {
  const settings = {
    card_background: 'rgba(8, 12, 24, 0.65)',
    primary_color: '#00d4ff',
    secondary_color: '#ff4081',
    card_rounding: 28,
    glass_blur: 20,
    background_type: 'animated',
    background_animation: 'pulse',
    background_color: '#030710',
    background_gradient: null,
    background_image: null,
    background_opacity: 1,
    ...customSettings
  };

  return createTheme({
    palette: {
      mode: 'dark',
      primary: {
        main: settings.primary_color,
        light: '#33e0ff',
        dark: '#0099cc',
        contrastText: '#ffffff',
      },
      secondary: {
        main: settings.secondary_color,
        light: '#ff79b0',
        dark: '#c60055',
        contrastText: '#ffffff',
      },
      success: {
        main: '#00ff88',
        light: '#33ffaa',
        dark: '#00cc66',
        contrastText: '#000000',
      },
      error: {
        main: '#ff4444',
        light: '#ff6b6b',
        dark: '#cc0000',
        contrastText: '#ffffff',
      },
      warning: {
        main: '#ffaa00',
        light: '#ffcc33',
        dark: '#cc8800',
        contrastText: '#000000',
      },
      info: {
        main: settings.primary_color,
        light: '#33e0ff',
        dark: '#0099cc',
        contrastText: '#ffffff',
      },
      background: {
        default: 'transparent',
        paper: settings.card_background,
      },
      text: {
        primary: '#ffffff',
        secondary: 'rgba(255, 255, 255, 0.7)',
        disabled: 'rgba(255, 255, 255, 0.5)',
      },
      divider: 'rgba(255, 255, 255, 0.1)',
    },
    typography: {
      fontFamily: "'Inter', 'Roboto', 'Helvetica', 'Arial', sans-serif",
      fontSize: 14,
      fontWeightLight: 300,
      fontWeightRegular: 400,
      fontWeightMedium: 500,
      fontWeightBold: 700,
      h1: { fontSize: '2.5rem', fontWeight: 700, letterSpacing: '-0.02em' },
      h2: { fontSize: '2rem', fontWeight: 700, letterSpacing: '-0.01em' },
      h3: { fontSize: '1.75rem', fontWeight: 600, letterSpacing: '-0.01em' },
      h4: { fontSize: '1.5rem', fontWeight: 600, letterSpacing: '-0.005em' },
      h5: { fontSize: '1.25rem', fontWeight: 600 },
      h6: { fontSize: '1rem', fontWeight: 600 },
      button: { fontSize: '0.875rem', fontWeight: 600, textTransform: 'none', letterSpacing: '0.02em' },
    },
    shape: {
      borderRadius: settings.card_rounding,
    },
    // Custom properties for easy access in styled components
    custom: {
      glassBlur: settings.glass_blur,
      cardRounding: settings.card_rounding,
      cardBackground: settings.card_background,
      backgroundType: settings.background_type,
      backgroundAnimation: settings.background_animation,
      backgroundColor: settings.background_color,
      backgroundGradient: settings.background_gradient,
      backgroundImage: settings.background_image,
      backgroundOpacity: settings.background_opacity,
    },
    shadows: Array(25).fill('none').map((_, i) => i === 0 ? 'none' : `0 ${i * 2}px ${i * 4}px rgba(0, 0, 0, 0.3)`),
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          body: {
            background: '#02040a',
            backgroundImage: 'radial-gradient(circle at 50% 50%, #0a0f1d 0%, #02040a 100%)',
            backgroundAttachment: 'fixed',
            margin: 0,
            padding: 0,
            minHeight: '100vh',
            '&::-webkit-scrollbar': { width: '6px' },
            '&::-webkit-scrollbar-thumb': { background: 'rgba(255, 255, 255, 0.1)', borderRadius: '10px' },
          },
        },
      },
      MuiCard: {
        styleOverrides: {
          root: {
            background: settings.card_background,
            backdropFilter: `blur(${settings.glass_blur}px)`,
            border: '1px solid rgba(255, 255, 255, 0.08)',
            borderRadius: `${settings.card_rounding}px`,
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
            transition: 'transform 0.3s ease, box-shadow 0.3s ease',
          },
        },
      },
      MuiButton: {
        styleOverrides: {
          root: {
            borderRadius: '12px',
            fontWeight: 600,
            textTransform: 'none',
            padding: '8px 20px',
          },
          contained: {
            background: settings.secondary_color,
            color: settings.secondary_color === '#ffffff' ? '#000' : '#fff',
            '&:hover': {
              background: settings.secondary_color,
              filter: 'brightness(1.1)',
              transform: 'translateY(-2px)',
            },
          },
        },
      },
    },
  });
};

const defaultTheme = getAppTheme();
export default defaultTheme;
